import React from 'react';
import dayjs from 'dayjs';
import { Select, InputNumber, DatePicker, Switch } from 'antd';
import type { InternalMedicineData } from '../../hooks/useConsultationForm';

interface Props {
  internalMedicineData: InternalMedicineData;
  setInternalMedicineField: (section: keyof InternalMedicineData, field: string, value: any) => void;
}

const DIABETES_TYPES = [
  { value: 'Tipo 1', label: 'Tipo 1' },
  { value: 'Tipo 2', label: 'Tipo 2' },
  { value: 'Gestacional', label: 'Gestacional' },
  { value: 'Otro', label: 'Otro' },
];

const TREATMENT_OPTIONS = [
  { value: 'Dieta', label: 'Dieta' },
  { value: 'Metformina', label: 'Metformina' },
  { value: 'Sulfonilurea', label: 'Sulfonilurea' },
  { value: 'Insulina', label: 'Insulina' },
  { value: 'Otro', label: 'Otro' },
];

const DiabetesAssessment: React.FC<Props> = ({ internalMedicineData, setInternalMedicineField }) => {
  const d = internalMedicineData.diabetes;
  const set = (field: string, value: any) => setInternalMedicineField('diabetes', field, value);

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">Diabetes Mellitus</h5>
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Tipo</label>
              <Select
                className="w-100"
                value={d.type || undefined}
                onChange={(v) => set('type', v)}
                placeholder="Seleccionar..."
                options={DIABETES_TYPES}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Años de Diagnóstico</label>
              <InputNumber
                className="w-100"
                min={0}
                max={80}
                value={d.diagnosis_years}
                onChange={(v) => set('diagnosis_years', v)}
                placeholder="0-80"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">HbA1c (%)</label>
              <InputNumber
                className="w-100"
                min={0}
                max={20}
                step={0.1}
                value={d.hba1c}
                onChange={(v) => set('hba1c', v)}
                placeholder="0-20"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Fecha HbA1c</label>
              <DatePicker
                className="w-100"
                value={d.hba1c_date ? dayjs(d.hba1c_date) : null}
                onChange={(date) => set('hba1c_date', date ? date.format('YYYY-MM-DD') : null)}
                format="YYYY-MM-DD"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Glucemia en Ayunas (mg/dL)</label>
              <InputNumber
                className="w-100"
                min={0}
                max={600}
                value={d.fasting_glucose}
                onChange={(v) => set('fasting_glucose', v)}
                placeholder="0-600"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Examen de Pie</label>
              <div className="d-flex align-items-center gap-2 mt-1">
                <Switch
                  checked={d.foot_exam_done}
                  onChange={(v) => set('foot_exam_done', v)}
                />
                <span className="text-muted small">{d.foot_exam_done ? 'Realizado' : 'No realizado'}</span>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Fondo de Ojo</label>
              <div className="d-flex align-items-center gap-2 mt-1">
                <Switch
                  checked={d.fundoscopy_done}
                  onChange={(v) => set('fundoscopy_done', v)}
                />
                <span className="text-muted small">{d.fundoscopy_done ? 'Realizado' : 'No realizado'}</span>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Cribado de Nefropatía</label>
              <div className="d-flex align-items-center gap-2 mt-1">
                <Switch
                  checked={d.nephropathy_screening_done}
                  onChange={(v) => set('nephropathy_screening_done', v)}
                />
                <span className="text-muted small">{d.nephropathy_screening_done ? 'Realizado' : 'No realizado'}</span>
              </div>
            </div>
          </div>
        </div>
        {d.foot_exam_done && (
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Hallazgos Examen de Pie</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={d.foot_exam_findings}
                  onChange={(e) => set('foot_exam_findings', e.target.value)}
                  placeholder="Describa hallazgos del examen de pie..."
                />
              </div>
            </div>
          </div>
        )}
        {d.fundoscopy_done && (
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Hallazgos Fondo de Ojo</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={d.fundoscopy_findings}
                  onChange={(e) => set('fundoscopy_findings', e.target.value)}
                  placeholder="Describa hallazgos del fondo de ojo..."
                />
              </div>
            </div>
          </div>
        )}
        {d.nephropathy_screening_done && (
          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Albuminuria (mg/g)</label>
                <InputNumber
                  className="w-100"
                  min={0}
                  value={d.albuminuria}
                  onChange={(v) => set('albuminuria', v)}
                  placeholder="mg/g"
                />
              </div>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Tratamiento Actual</label>
              <Select
                className="w-100"
                mode="multiple"
                value={d.current_treatment}
                onChange={(v) => set('current_treatment', v)}
                placeholder="Seleccionar tratamientos..."
                options={TREATMENT_OPTIONS}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Observaciones</label>
              <textarea
                className="form-control"
                rows={2}
                value={d.observations}
                onChange={(e) => set('observations', e.target.value)}
                placeholder="Notas adicionales sobre diabetes..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiabetesAssessment;
